import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { getCurrentUser } from 'redux/selectors/users';

import { AngularContext } from 'react-app';

import ConversationModalContent from 'lecture_pages/components/ai-role-play/ai-conversation-modal';
import AiConversationContext, {
  AIRolePlayStatus,
  AudioRecordingStatus,
  Message,
  RolePlayInteractionMode,
  SessionData,
} from 'lecture_pages/components/ai-role-play/ai-conversation-modal/context';
import useAiConversationHandlers from 'lecture_pages/components/ai-role-play/hooks/use-ai-conversation-handlers';
import useAiRolePlaySubmissionPusherEvents from 'lecture_pages/components/ai-role-play/hooks/use-ai-role-play-submission-pusher-events';
import { RootState } from 'redux/schemas';
import useAiRolePlay from 'lecture_pages/components/ai-role-play/hooks/use-ai-role-play';
import useVoiceAiConversationHandlers from 'lecture_pages/components/ai-role-play/hooks/use-voice-ai-conversation-handlers';
import RolePlaySubmissionModalStyles from './role-play-submission-modal-styles';

const NewConversationView = () => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [$state, PageLevelManager] = injectServices(['$state', 'PageLevelManager']);
  const rolePlayId = Number($state?.params?.rolePlayId);

  const aiRolePlay = useAiRolePlay(rolePlayId);

  const { courseCatalogId, courseId, interactionMode } = aiRolePlay ?? {};

  const { anonymizedIdentifier } = useSelector((state: RootState) => getCurrentUser(state));

  const [aiRolePlayStatus, setAiRolePlayStatus] = useState(AIRolePlayStatus.NOT_STARTED);
  const [showScenarioDetails, setShowScenarioDetails] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTimeout, setIsTimeout] = useState(false);
  const [isRolePlayStarting, setIsRolePlayStarting] = useState(false);
  const [openingNarrative, setOpeningNarrative] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const [sessionData, setSessionData] = useState<SessionData>({
    aiApiEndpoint: null,
    jwtToken: null,
    submissionId: null,
    expiresAt: null,
  });
  const [audioRecordingStatus, setAudioRecordingStatus] = useState(AudioRecordingStatus.NOT_STARTED);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(false);


  const audioStreamRef = useRef(null);
  const recorderRef = useRef(null);

  const isHardDeadlineExpired = !!(aiRolePlay?.hardDeadline && aiRolePlay?.expired);

  const {
    handleChunk,
    handleError,
    handleStartConversation,
  } = useAiConversationHandlers({
    aiRolePlayId: aiRolePlay?.id,
    setAiRolePlayStatus,
    setShowScenarioDetails,
    messages,
    setMessages,
    sessionData,
    setSessionData,
    setIsTimeout,
    abortControllerRef,
    setIsRolePlayStarting,
    openingNarrative,
    setOpeningNarrative,
  });

  const {
    handleStartVoiceConversation,
    aiVoiceCardState,
    learnerVoiceCardState,
    learnerMediaStream,
    voiceRecordingState,
  } = useVoiceAiConversationHandlers({
    aiRolePlayId: aiRolePlay?.id,
    setAiRolePlayStatus,
    setShowScenarioDetails,
    sessionData,
    setSessionData,
    setIsTimeout,
    abortControllerRef,
    setIsRolePlayStarting,
    setMicrophoneEnabled,
    audioStreamRef,
    recorderRef,
    setOpeningNarrative,
  });

  useAiRolePlaySubmissionPusherEvents({
    anonymizedIdentifier,
    courseId,
    aiRolePlayId: aiRolePlay?.id,
    courseCatalogId,
    pointsReceived: aiRolePlay?.pointsReceived,
    skillTags: [],
  });

  const handleExitChat = useCallback((skipConfirmation = false) => {
    const exit = () => {
      const lastParent = PageLevelManager?.lastParent?.();
      if (lastParent?.state?.name) {
        $state.go(lastParent.state.name, lastParent.params);
      } else {
        $state.go('dashboard');
      }
    };
    if (!skipConfirmation && aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS) {
      dispatch(openConfirmationDialog({
        title: t.FORM.ARE_YOU_SURE(),
        bodyText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.MESSAGE(),
        confirmText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.CONFIRM(),
        cancelText: t.FORM.CANCEL(),
        onConfirm: exit,
      }));
    } else {
      exit();
    }
  }, [aiRolePlayStatus, dispatch, PageLevelManager, $state]);

  useEffect(() => () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const setShowConversationModal = useCallback(() => undefined, []);

  const wrapperStyles = css`
    height: 100%;
    > div {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  `;

  return (
    <>
      <RolePlaySubmissionModalStyles />
      <AiConversationContext.Provider
        value={{
          interactionMode,
          handleExitChat,
          aiRolePlayStatus,
          setAiRolePlayStatus,
          showScenarioDetails,
          setShowScenarioDetails,
          setShowConversationModal,
          aiRolePlayId: aiRolePlay.id,
          messages,
          setMessages,
          sessionData,
          setSessionData,
          isTimeout,
          setIsTimeout,
          abortControllerRef,
          isHardDeadlineExpired,
          isRolePlayStarting,
          setIsRolePlayStarting,
          openingNarrative,
          handleChunk,
          handleError,
          handleStartConversation,
          handleStartVoiceConversation,
          audioRecordingStatus,
          setAudioRecordingStatus,
          microphoneEnabled,
          aiVoiceCardState,
          learnerVoiceCardState,
          learnerMediaStream,
          voiceRecordingState,
        }}
      >
        <div css={wrapperStyles}>
          <ConversationModalContent />
        </div>
      </AiConversationContext.Provider>
    </>
  );
};

export default NewConversationView;
